import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ax/Button";
import {
  checkAdminClaim,
  adminGetMetrics,
  adminGrant,
  adminSetFlag,
  adminGetGrants,
} from "@/lib/adminStore";

const STATUS_OPTIONS = [
  "pilot",
  "trialing",
  "active",
  "past_due",
  "expired",
  "canceled",
  "none",
];

export default function Admin() {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [metrics, setMetrics] = useState({
    usersCount: 0,
    decisionsCount: 0,
    publishedBooksCount: 0,
    aiCallsCountThisMonth: 0,
    aiEnabled: true,
  });
  const [grants, setGrants] = useState([]);
  const [targetUid, setTargetUid] = useState("");
  const [targetStatus, setTargetStatus] = useState("pilot");
  const [busyFlag, setBusyFlag] = useState(false);
  const [busyGrant, setBusyGrant] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");

    const [metricsRes, grantsRes] = await Promise.all([
      adminGetMetrics(),
      adminGetGrants(),
    ]);

    if (metricsRes.error === "unauthorized" || grantsRes.error === "unauthorized") {
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    if (metricsRes.error) {
      setErrorMsg("Gagal memuat metrik admin.");
      setLoading(false);
      return;
    }

    setIsAdmin(true);
    if (metricsRes.metrics) {
      setMetrics(metricsRes.metrics);
    }
    if (grantsRes.grants) {
      setGrants(grantsRes.grants);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAdminClaim().then((hasClaim) => {
      if (!hasClaim) {
        // Coba loadData untuk verifikasi via error callable
        loadData();
      } else {
        setIsAdmin(true);
        loadData();
      }
    });
  }, [loadData]);

  const handleToggleAi = async () => {
    setBusyFlag(true);
    setErrorMsg("");
    setSuccessMsg("");
    const nextVal = !metrics.aiEnabled;
    const res = await adminSetFlag({ flag: "aiEnabled", value: nextVal });
    setBusyFlag(false);
    if (res.ok) {
      setMetrics((prev) => ({ ...prev, aiEnabled: res.aiEnabled }));
      setSuccessMsg(
        res.aiEnabled
          ? "Inferensi AI diaktifkan kembali."
          : "Inferensi AI dijeda global (panggilan baru ditolak)."
      );
    } else {
      setErrorMsg(res.error || "Gagal mengubah saklar AI.");
    }
  };

  const handleGrantSubmit = async (e) => {
    e.preventDefault();
    if (!targetUid.trim()) {
      setErrorMsg("UID pengguna wajib diisi.");
      return;
    }

    setBusyGrant(true);
    setErrorMsg("");
    setSuccessMsg("");

    const res = await adminGrant({ uid: targetUid.trim(), status: targetStatus });
    setBusyGrant(false);

    if (res.ok) {
      setSuccessMsg(`Hak akses pengguna "${targetUid.trim()}" diubah menjadi "${targetStatus}".`);
      setTargetUid("");
      // Refresh list grants
      const grantsRes = await adminGetGrants();
      if (grantsRes.grants) {
        setGrants(grantsRes.grants);
      }
    } else {
      setErrorMsg(res.error || "Gagal memberikan hak akses.");
    }
  };

  const handleQuickGrant = async (uid, status) => {
    setBusyGrant(true);
    setErrorMsg("");
    setSuccessMsg("");

    const res = await adminGrant({ uid, status });
    setBusyGrant(false);

    if (res.ok) {
      setSuccessMsg(
        status === "expired"
          ? `Hak akses pengguna "${uid}" telah dicabut (expired).`
          : `Hak akses pengguna "${uid}" telah dipulihkan (pilot).`
      );
      const grantsRes = await adminGetGrants();
      if (grantsRes.grants) {
        setGrants(grantsRes.grants);
      }
    } else {
      setErrorMsg(res.error || "Gagal mengubah hak akses.");
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-loading" className="py-12 text-center text-ax-muted">
        Memuat...
      </div>
    );
  }

  // Jika bukan admin: Halaman ini tidak tersedia
  if (!isAdmin) {
    return (
      <div data-testid="admin-unauthorized" className="py-12 text-center">
        <p className="text-base text-ax-text">Halaman ini tidak tersedia.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="flex flex-col gap-8 pb-16">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="type-h1 m-0 text-xl font-semibold text-ax-text">Admin</h1>
            <span className="rounded-[4px] bg-ax-primary/10 px-2 py-0.5 text-xs font-mono font-medium text-ax-primary">
              ADMIN
            </span>
          </div>
          <p className="caption m-0 mt-1 text-sm text-ax-muted">
            Metrik agregat, kontrol inferensi AI, dan manajemen hak langganan.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/library"
            data-testid="admin-cms-library-link"
            className="inline-flex min-h-[36px] items-center rounded-[8px] border border-ax-border bg-ax-surface px-3 text-sm font-medium text-ax-text transition hover:bg-ax-bg"
          >
            CMS Library →
          </Link>
        </div>
      </div>

      {/* Alerts */}
      {errorMsg && (
        <div role="alert" className="rounded-[8px] border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400">
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div role="status" className="rounded-[8px] border border-emerald-500/20 bg-emerald-500/10 p-3 text-sm text-emerald-400">
          {successMsg}
        </div>
      )}

      {/* 1. Kartu Metrik (Hanya Hitungan, Tanpa Isi) */}
      <section data-testid="admin-metrics-section">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ax-muted">
          Metrik Sistem
        </h2>
        <div className="mt-3 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div
            data-testid="metric-users"
            className="flex flex-col rounded-[12px] border border-ax-border bg-ax-surface p-4 shadow"
          >
            <span className="caption text-ax-muted">Pengguna</span>
            <span className="mt-2 font-mono text-2xl font-bold text-ax-text">
              {metrics.usersCount}
            </span>
          </div>

          <div
            data-testid="metric-decisions"
            className="flex flex-col rounded-[12px] border border-ax-border bg-ax-surface p-4 shadow"
          >
            <span className="caption text-ax-muted">Keputusan</span>
            <span className="mt-2 font-mono text-2xl font-bold text-ax-text">
              {metrics.decisionsCount}
            </span>
          </div>

          <div
            data-testid="metric-books"
            className="flex flex-col rounded-[12px] border border-ax-border bg-ax-surface p-4 shadow"
          >
            <span className="caption text-ax-muted">Buku Published</span>
            <span className="mt-2 font-mono text-2xl font-bold text-ax-text">
              {metrics.publishedBooksCount}
            </span>
          </div>

          <div
            data-testid="metric-ai-calls"
            className="flex flex-col rounded-[12px] border border-ax-border bg-ax-surface p-4 shadow"
          >
            <span className="caption text-ax-muted">Panggilan AI Bulan Ini</span>
            <span className="mt-2 font-mono text-2xl font-bold text-ax-text">
              {metrics.aiCallsCountThisMonth}
            </span>
          </div>
        </div>
      </section>

      {/* 2. Saklar aiEnabled */}
      <section data-testid="admin-ai-flag-section" className="rounded-[12px] border border-ax-border bg-ax-surface p-5 shadow">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="m-0 text-base font-semibold text-ax-text">Saklar Inferensi AI</h3>
              <span
                data-testid="flag-ai-enabled-status"
                className={`rounded-[4px] px-2 py-0.5 text-xs font-mono font-medium ${
                  metrics.aiEnabled
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-amber-500/10 text-amber-400"
                }`}
              >
                {metrics.aiEnabled ? "AI Aktif" : "AI Dijeda"}
              </span>
            </div>
            <p className="caption m-0 mt-1 text-sm text-ax-muted">
              {metrics.aiEnabled
                ? "Panggilan model AI (composeBrief, continuityCheck, intake, challenge) beroperasi normal."
                : "Inferensi AI dimatikan global. Panggilan AI baru ditolak. Brief yang ada tidak dihapus."}
            </p>
          </div>
          <Button
            data-testid="flag-ai-enabled-toggle"
            variant={metrics.aiEnabled ? "secondary" : "primary"}
            disabled={busyFlag}
            onClick={handleToggleAi}
          >
            {metrics.aiEnabled ? "Jeda Inferensi AI" : "Aktifkan Inferensi AI"}
          </Button>
        </div>
      </section>

      {/* 3. Formulir Grant Hak Akses */}
      <section data-testid="admin-grant-form-section" className="rounded-[12px] border border-ax-border bg-ax-surface p-5 shadow">
        <h3 className="m-0 text-base font-semibold text-ax-text">Grant Hak Langganan</h3>
        <p className="caption m-0 mt-1 text-sm text-ax-muted">
          Ubah subscription.status untuk pengguna yang ditentukan.
        </p>

        <form data-testid="admin-grant-form" onSubmit={handleGrantSubmit} className="mt-4 flex flex-col gap-4 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="caption block text-xs font-medium text-ax-muted">
              UID Pengguna
            </label>
            <input
              type="text"
              required
              data-testid="grant-uid-input"
              value={targetUid}
              onChange={(e) => setTargetUid(e.target.value)}
              placeholder="Misal: nxhdOsg6lRjqCpcQyrkZPSnHvfPU"
              className="mt-1 w-full rounded-[8px] border border-ax-border bg-ax-bg px-3 py-2 font-mono text-sm text-ax-text outline-none focus:border-ax-primary"
            />
          </div>

          <div className="w-full md:w-48">
            <label className="caption block text-xs font-medium text-ax-muted">
              Status Hak Akses
            </label>
            <select
              data-testid="grant-status-select"
              value={targetStatus}
              onChange={(e) => setTargetStatus(e.target.value)}
              className="mt-1 w-full rounded-[8px] border border-ax-border bg-ax-bg px-3 py-2 text-sm text-ax-text outline-none focus:border-ax-primary"
            >
              {STATUS_OPTIONS.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </div>

          <Button
            type="submit"
            data-testid="grant-submit-button"
            disabled={busyGrant || !targetUid.trim()}
          >
            {busyGrant ? "Menyimpan..." : targetStatus === "expired" ? "Cabut Akses" : "Beri Hak Akses"}
          </Button>
        </form>
      </section>

      {/* 4. Tabel Grant (Hanya UID, Status, dan Waktu) */}
      <section data-testid="admin-grants-table-section">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ax-muted">
          Riwayat Grant Hak Akses
        </h2>
        <div className="mt-3 overflow-x-auto rounded-[12px] border border-ax-border bg-ax-surface shadow">
          <table data-testid="admin-grants-table" className="w-full text-left text-sm">
            <thead className="border-b border-ax-border bg-ax-bg/50 text-xs uppercase tracking-wider text-ax-muted">
              <tr>
                <th className="px-4 py-3 font-medium">UID</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Waktu</th>
                <th className="px-4 py-3 font-medium">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ax-border text-ax-text">
              {grants.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-ax-muted">
                    Belum ada riwayat grant.
                  </td>
                </tr>
              ) : (
                grants.map((g) => (
                  <tr key={g.uid} data-testid={`grant-row-${g.uid}`}>
                    <td
                      data-testid={`grant-row-uid-${g.uid}`}
                      className="px-4 py-3 font-mono text-xs text-ax-text"
                    >
                      {g.uid}
                    </td>
                    <td
                      data-testid={`grant-row-status-${g.uid}`}
                      className="px-4 py-3 font-mono text-xs"
                    >
                      <span className="rounded-[4px] bg-ax-primary/10 px-2 py-0.5 text-ax-primary">
                        {g.status}
                      </span>
                    </td>
                    <td
                      data-testid={`grant-row-time-${g.uid}`}
                      className="px-4 py-3 font-mono text-xs text-ax-muted"
                    >
                      {new Date(g.updatedAt).toLocaleString("id-ID")}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {g.status === "expired" ? (
                        <button
                          type="button"
                          data-testid={`grant-action-restore-${g.uid}`}
                          onClick={() => handleQuickGrant(g.uid, "pilot")}
                          disabled={busyGrant}
                          className="rounded-[6px] border border-ax-border bg-ax-surface px-2.5 py-1 text-xs font-medium text-ax-link hover:underline"
                        >
                          Beri lagi (pilot)
                        </button>
                      ) : (
                        <button
                          type="button"
                          data-testid={`grant-action-revoke-${g.uid}`}
                          onClick={() => handleQuickGrant(g.uid, "expired")}
                          disabled={busyGrant}
                          className="rounded-[6px] border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-400 hover:bg-red-500/20"
                        >
                          Cabut
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
